import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { StatusBadge } from '@Components/ConnectedChainCard';
import ChainAvatar from '@Components/ConnectedChainCard/chainAvatar';
import { CredentialEntry } from '../credentials.types';

interface CredentialListViewProps {
  entries: CredentialEntry[];
  renderRowActions: (entry: CredentialEntry) => React.ReactNode;
}

const COLS = 'grid-cols-[1.4fr_120px_1.2fr_96px_200px]';

const CredentialListView = ({ entries, renderRowActions }: CredentialListViewProps) => {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-[0_1px_3px_rgba(16,24,40,.06)]">
      <div className="min-w-[700px]">
        <div
          className={cn(
            'grid gap-3 border-b border-gray-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400',
            COLS,
          )}>
          <div>{t('Credentials.List.Retailer')}</div>
          <div>{t('Credentials.List.Status')}</div>
          <div>{t('Credentials.List.User')}</div>
          <div>{t('Credentials.List.Updated')}</div>
          <div className="text-right">{t('Credentials.List.Actions')}</div>
        </div>
        {entries.map(({ card, raw, id }, i) => {
          const isAvailable = card.status === 'available';
          const userText = card.userLabel ?? '—';
          const updatedText = card.lastSyncLabel;
          return (
            <div
              key={id}
              className={cn(
                'grid gap-3 px-4 py-3 text-sm items-center transition-colors hover:bg-gray-50',
                COLS,
                i > 0 && 'border-t border-gray-100',
              )}>
              <div className="flex items-center gap-3 min-w-0">
                <ChainAvatar {...card.avatar} size="sm" />
                <div className="min-w-0">
                  <div className="truncate font-semibold text-gray-900">{card.name}</div>
                  <div className="truncate text-xs text-gray-500">{card.platform}</div>
                </div>
              </div>
              <div>
                <StatusBadge status={card.status} />
              </div>
              <div className="min-w-0 truncate font-mono text-xs text-gray-700">{isAvailable ? '—' : userText}</div>
              <div className="whitespace-nowrap text-xs text-gray-500">{isAvailable ? '—' : updatedText}</div>
              <div className="flex items-center justify-end gap-2">{renderRowActions({ card, raw, id })}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CredentialListView;
